import { ComponentChild, ComponentProps, createRef } from 'preact';
import { useCallback, useRef, useEffect } from 'preact/hooks';
import type { BundleType } from '../resources/nls/bundle';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { getClientHints } from '../utils/PRIVATE_clientHints';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { classNames } from '../utils/UNSAFE_classNames';
import {
  NavigationListItem,
  NavigationList,
  RemovableNavigationListItem,
  FocusableHandle,
  NavigationListLinkItem
} from '../UNSAFE_NavigationList';
import { OverflowH } from '../UNSAFE_RedwoodIcons/OverflowH';
import { useTabBarContext } from './useTabBarContext';
import { Dropdown } from '../UNSAFE_Dropdown';
import { Sheet } from '../UNSAFE_Sheet';
import { BaseTabBarItem } from './BaseTabBarItem';
import { styles } from './themes/OverflowTabBarItemStyles.css';
import { TabBarItem } from './TabBarItem';

export type OverflowSelectionDetail<K> = {
  value: K;
};

export type CloseDetail = {
  /**
   * dismissed represents dismissing the DropDown
   */
  reason: 'dismissed' | 'itemAction' | 'outsideMousedown';
};

export type Item<K extends string | number> = Omit<
  ComponentProps<typeof TabBarItem<K>>,
  'testId' | 'aria-controls'
> & {
  isRemovable?: boolean;
  /**
   * Accepts the tabpanel element's ID associated with the tab item
   **/
  tabPanelId?: string;
  /**
   * Sets the URL that the hyperlink points to.
   */
  href?: string;
};

export type OverflowTabBarItemProps<K extends string | number> = {
  /**
   * Key of the TabBarItem
   */
  overflowItemKey: K;
  /**
   * Whether to open the popup
   */
  isOpen: boolean;
  /**
   * Property that triggers a callback when DropDown is supposed to be closed.
   */
  onClose?: (detail: CloseDetail) => void;
  /**
   * The icon of the OverflowTabBarItem
   */
  icon?: ComponentChild;
  /**
   * The content to be rendered inside the Badge component.
   */
  badge?: string;
  /**
   * An array of data used by NavigationList to render individual overflow items.
   */
  overflowItems?: Item<K>[];
};

/**
 * An OverflowTabBarItem renders TabBarItems in a device appropriate popup. OverflowTabBarItem is used internally in an OverflowTabBar.
 */

export function OverflowTabBarItem<K extends string | number>({
  overflowItemKey,
  icon,
  badge,
  overflowItems,
  isOpen,
  onClose
}: OverflowTabBarItemProps<K>) {
  const { selection, showFocusRing, isEdgeBottom, onSelect, onRemove, layout } = useTabBarContext();

  const isPointerSelection = useRef<boolean>(false);

  // Reset pointer selection flag when the popup closes so keyboard navigation
  // restores focus-visible on the overflow trigger.
  useEffect(() => {
    if (!isOpen) {
      isPointerSelection.current = false;
    }
  }, [isOpen]);

  const overflowTabBarItemRef = useRef<HTMLDivElement>(null);
  const navigationListRef = createRef<FocusableHandle>();

  const handleSelectionChange = useCallback<
    Required<ComponentProps<typeof NavigationList>>['onSelectionChange']
  >(
    (detail: { value: string | number; reason?: 'pointer' | 'keyboard' }) => {
      if (detail.reason === 'pointer') {
        isPointerSelection.current = true;
      }
      onSelect?.({ value: detail.value });
      onClose?.({ reason: 'itemAction' });
    },
    [onSelect, onClose]
  );

  const handleDropdownClose = useCallback(
    (e: CloseDetail) => {
      onClose?.({ reason: e.reason });
    },
    [onClose]
  );

  const handleSheetClose = useCallback(() => {
    onClose?.({ reason: 'dismissed' });
  }, [onClose]);

  //When NavigationList steals focus the app resets it. Not always would it be reset to
  //parent of OverflowTabBarItem. Though currentKey would be correct, but focusRing would would not be visible
  //Hence we check for isPointerSelection.current

  const focusRingRequirement = showFocusRing && !isPointerSelection.current && !getIsMobile();

  const overflowContainsKey = (searchKey: K) => {
    return overflowItems?.find((item) => item.itemKey === searchKey) != null;
  };

  const currentPanelId = overflowItems?.find((item) => item.itemKey === selection)?.tabPanelId;

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');

  const isSelected = selection !== undefined && overflowContainsKey(selection as K);

  //instead of specifying flex: '1 0 auto' in cssProps var
  const styleInterpolations = mergeInterpolations<FlexitemProps>([
    ...Object.values(flexitemInterpolations)
  ]);
  const flexDimensions = styleInterpolations({
    flex: layout === 'stretch' ? '1 0 auto' : '0 0 auto'
  });

  const navContainerClasses = classNames([styles.navContainerBase]);

  const navContainerRef = useRef<HTMLDivElement>(null);
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        navigationListRef.current?.focus();
      }
    },
    [navigationListRef]
  );
  const getNavigationList = useCallback(() => {
    return (
      <div onKeyDown={handleKeyDown} tabIndex={0} ref={navContainerRef} class={navContainerClasses}>
        <NavigationList
          ref={navigationListRef}
          selection={selection}
          onSelectionChange={handleSelectionChange}
          onRemove={onRemove}>
          {overflowItems?.map((item) =>
            item.href != null ? (
              <NavigationListLinkItem
                href={item.href}
                itemKey={item.itemKey}
                label={item.label}
                badge={item.badge}
                metadata={item.metadata}
                severity={item.severity}
              />
            ) : item.isRemovable ? (
              <RemovableNavigationListItem
                itemKey={item.itemKey}
                label={item.label}
                badge={item.badge}
                metadata={item.metadata}
                severity={item.severity}
              />
            ) : (
              <NavigationListItem
                itemKey={item.itemKey}
                label={item.label}
                badge={item.badge}
                metadata={item.metadata}
                severity={item.severity}
              />
            )
          )}
        </NavigationList>
      </div>
    );
  }, [
    handleKeyDown,
    navContainerClasses,
    navigationListRef,
    selection,
    handleSelectionChange,
    onRemove,
    overflowItems
  ]);

  return (
    <>
      {overflowItems && overflowItems.length > 0 && (
        <div ref={overflowTabBarItemRef} style={flexDimensions}>
          <BaseTabBarItem
            aria-controls={currentPanelId}
            itemKey={overflowItemKey}
            label={translations.overflowItemLabel()}
            icon={icon ? icon : <OverflowH />}
            badge={badge}
            showFocusRingOverride={focusRingRequirement}
            selectionOverride={isSelected}
            displayOverride={'icons'}
            aria-haspopup={true}
          />
        </div>
      )}
      {getIsMobile() ? (
        <Sheet isOpen={isOpen} onClose={handleSheetClose} initialFocus="firstFocusable">
          {getNavigationList()}
        </Sheet>
      ) : (
        <Dropdown
          initialFocus="firstFocusable"
          isOpen={isOpen}
          onClose={handleDropdownClose}
          placement={isEdgeBottom ? 'top-end' : 'bottom-end'}
          anchorRef={overflowTabBarItemRef}>
          {getNavigationList()}
        </Dropdown>
      )}
    </>
  );
}

const getIsMobile = () => {
  const { deviceRenderMode } = getClientHints();
  return deviceRenderMode === 'phone';
};
